import { Button } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import type { ApplicationModel } from "../../../../viewModels/Applications/ApplicationModel";
import type { AttachDocumentFormModel } from "../../../../viewModels/Documents/AttachDocumentModel";
import { useCargoPlanning } from "../CargoPlanningProvider";
import { areCargoPlanningDocumentsEqual, getCargoPlanningDocumentIds, isCargoPlanningWaiting } from "../../utils/cargoPlanning";
import slice from "../../reducer";

type Props = {
  application: ApplicationModel;
  documents?: AttachDocumentFormModel[];
  prepareApplication?: () => ApplicationModel;
  label?: string;
};

const CargoPlanningButton = ({
  application,
  documents = application.cargoInfo?.cargoDimensionsDocuments ?? [],
  prepareApplication,
  label,
}: Props) => {
  const state = useCargoPlanning();
  const dispatch = useDispatch();
  const { t } = useTranslation("translations");
  if (!state || state.applicationId !== application.id || application.type !== "FTL") return null;

  const documentIds = getCargoPlanningDocumentIds(documents);
  const documentsChanged = !areCargoPlanningDocumentsEqual(documentIds, state.savedDocumentIds);
  const uploading = state.uploadingFiles > 0 || documents.some((document) => document.status === "uploading");
  const waiting = state.saving || (!state.statusError && (state.checking || isCargoPlanningWaiting(state)));
  const needsSave = documentsChanged || state.syncState === "NOT_STARTED";
  const handleClick = () => {
    dispatch(slice.actions.openCargoPlanning());
    if (state.saving || state.syncState === "SYNCING") return;
    if (needsSave) {
      dispatch(slice.actions.changeApplication(prepareApplication ? prepareApplication() : application));
      dispatch(slice.actions.updateApplication());
    } else if (!state.confirmed) {
      dispatch(slice.actions.checkCargoPlanning());
    }
  };

  return (
    <Button
      block
      type="primary"
      onClick={handleClick}
      disabled={uploading || (documentIds.length === 0 && !state.dealId)}
      icon={waiting ? <LoadingOutlined spin /> : undefined}
      style={{ height: "auto", minHeight: 32, whiteSpace: "normal" }}
    >
      {label ?? t("check_cargo_characteristics")}
    </Button>
  );
};

export default CargoPlanningButton;
